import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import api from "@/lib/axios"
import FormHeader from "@/components/FormHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useState } from "react"
import { FaPlus } from "react-icons/fa6"
import { Loader2 } from "lucide-react"
import { JenisBiaya, JenisUkt, SubStatusPlaza } from "@/types"
import { MdModeEdit } from "react-icons/md"
import { RiDeleteBack2Line } from "react-icons/ri"
import toast from "react-hot-toast"
import { AxiosError } from "axios"
import { getListJenisBiaya, getListSubStatusPlaza } from "@/services/getData"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import Combobox from "@/components/Combobox"

const postData = async (newData: JenisUkt) => {
  const response = await api.post("/master/jenis-ukt/store", newData)
  return response.data
}

const patchData = async (newData: JenisUkt) => {
  const response = await api.patch("/master/jenis-ukt/update", newData)
  return response.data
}

const deleteData = async (deletedData: { id_ref_jenis_ukt: string }) => {
  const response = await api({
    url: "/master/jenis-ukt/delete",
    method: "DELETE",
    data: deletedData,
  })
  return response.data
}

export default function Form({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE"
  selectedRow?: JenisUkt | null
  closeModal: () => void
}) {
  const [openListSubStatusPlaza, setOpenListSubStatusPlaza] = useState(false)

  const [form, setForm] = useState<JenisUkt>({
    id_ref_jenis_ukt: selectedRow?.id_ref_jenis_ukt || "",
    id_ref_jenis_pembiayaan: selectedRow?.id_ref_jenis_pembiayaan || "",
    kode_jenis_ukt: selectedRow?.kode_jenis_ukt || "",
    kode_jenis_ukt_sipepeng: selectedRow?.kode_jenis_ukt_sipepeng || "",
    nama_jenis_ukt: selectedRow?.nama_jenis_ukt || "",
    kode_sub_status_plaza: selectedRow?.kode_sub_status_plaza || "",
    keterangan: selectedRow?.keterangan || "",
    is_aktif: selectedRow?.is_aktif ?? true,
  })

  const isPost = method === "POST"
  const isPatch = method === "PATCH"
  const isDelete = method === "DELETE"

  const { data: dataJenisPembiayaan } = useQuery({
    queryKey: ["listJenisBiaya"],
    queryFn: getListJenisBiaya,
  })

  const { data: listSubStatusPlaza, isPending: isPendingListSubStatusPlaza } =
    useQuery({
      queryKey: ["listSubStatusPlaza"],
      queryFn: getListSubStatusPlaza,
      select: (data) =>
        data?.map((substatus: SubStatusPlaza) => {
          return {
            label: substatus.kode_sub_status,
            value: `${substatus.kode_sub_status}`,
          }
        }),
    })

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listJenisUkt"] })
      closeModal()
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mutate(form)
  }

  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Delete Data Jenis UKT"
          description={`Apakah anda yakin akan menghapus data "${selectedRow?.nama_jenis_ukt}"?`}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </div>
      </form>
    )

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={isPost ? "Insert Data Jenis UKT" : "Update Data Jenis UKT"}
      />

      <div>
        <Label>Jenis Pembiayaan</Label>
        <Select
          value={form.id_ref_jenis_pembiayaan}
          onValueChange={(value) =>
            setForm((prev) => ({
              ...prev,
              id_ref_jenis_pembiayaan: value,
            }))
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Pilih Jenis Pembiayaan">
              {form.id_ref_jenis_pembiayaan &&
                dataJenisPembiayaan?.find(
                  (jenisPembiayaan: JenisBiaya) =>
                    jenisPembiayaan.id_ref_jenis_pembiayaan ===
                    form.id_ref_jenis_pembiayaan
                )?.nama_pembiayaan}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {dataJenisPembiayaan?.map((item: JenisBiaya, index: number) => (
              <SelectItem key={index} value={`${item.id_ref_jenis_pembiayaan}`}>
                {item.nama_pembiayaan}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Kode Jenis UKT</Label>
        <Input
          maxLength={2}
          placeholder="Ketik kode jenis ukt..."
          value={form.kode_jenis_ukt}
          onChange={(event) =>
            setForm((prev) => ({ ...prev, kode_jenis_ukt: event.target.value }))
          }
        />
      </div>
      <div>
        <Label>Kode Jenis UKT Sipepeng</Label>
        <Input
          placeholder="Ketik kode jenis ukt sipepeng..."
          value={form.kode_jenis_ukt_sipepeng}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              kode_jenis_ukt_sipepeng: event.target.value,
            }))
          }
        />
      </div>
      <div>
        <Label>Nama Jenis UKT</Label>
        <Input
          placeholder="Ketik nama jenis ukt..."
          value={form.nama_jenis_ukt}
          onChange={(event) =>
            setForm((prev) => ({ ...prev, nama_jenis_ukt: event.target.value }))
          }
        />
      </div>
      <div>
        <Label>Sub Status Plaza</Label>
        <Combobox
          isPending={isPendingListSubStatusPlaza}
          open={openListSubStatusPlaza}
          setOpen={setOpenListSubStatusPlaza}
          value={form.kode_sub_status_plaza || ""}
          setValue={(value) => {
            setForm((prev) => ({ ...prev, kode_sub_status_plaza: value }))
          }}
          options={listSubStatusPlaza}
          placeholder="Pilih Sub Status Plaza"
        />
      </div>
      <div>
        <Label>Keterangan</Label>
        <Input
          placeholder="Ketik keterangan..."
          value={form.keterangan}
          onChange={(event) =>
            setForm((prev) => ({ ...prev, keterangan: event.target.value }))
          }
        />
      </div>

      <div className="flex gap-3">
        <Switch
          id="is_aktif"
          checked={form.is_aktif}
          onCheckedChange={() =>
            setForm((prev) => ({
              ...prev,
              is_aktif: !prev.is_aktif,
            }))
          }
        />
        <Label htmlFor="is_aktif">Status (nonaktif/aktif)</Label>
      </div>

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  )
}
